import { useLayoutEffect, useRef, useSyncExternalStore, type RefObject } from 'react';
import { SlashCommandMenu } from './SlashCommandMenu';
import type { SlashMenuStore } from './slash-menu-store';

interface SlashMenuHostProps {
  store: SlashMenuStore;
  /** The editor's `position: relative` wrapper; the menu is positioned against it. */
  anchorRef: RefObject<HTMLDivElement | null>;
}

/**
 * Renders the slash menu inside the editor subtree, positioned absolutely against
 * the editor wrapper. Because the wrapper scrolls together with the caret inside a
 * Dialog, the menu stays glued to the caret without per-scroll repositioning, and
 * it sits OUTSIDE the (possibly `overflow-hidden`) editor box so it never clips.
 */
export function SlashMenuHost({ store, anchorRef }: SlashMenuHostProps) {
  const state = useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);
  const menuRef = useRef<HTMLDivElement>(null);

  // Position imperatively in a layout effect: ref reads are disallowed during render,
  // and the store pushes a fresh snapshot on every caret move so this stays in sync.
  useLayoutEffect(() => {
    const node = menuRef.current;
    if (!node || !state.open) return;
    const caret = state.getRect();
    const anchor = anchorRef.current?.getBoundingClientRect();
    if (!caret || !anchor) return;
    node.style.left = `${caret.left - anchor.left}px`;
    node.style.top = `${caret.bottom - anchor.top + 6}px`;
  }, [state, anchorRef]);

  if (!state.open) return null;

  return (
    <div ref={menuRef} className="absolute left-0 top-0 z-50">
      <SlashCommandMenu
        items={state.items}
        query={state.query}
        onSelect={state.onSelect}
        onClose={state.onClose}
      />
    </div>
  );
}
